import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useCurrentUser } from '@/features/auth/state/auth'
import { useFetchAgreementByIdUser, useUpdateAgreementUser } from '@/lib/hooks/agreement'
import { useFetchSignatureByIdUser } from '@/features/signatures/services'
import { useFetchHistoryAgreementById } from '../services/useFetchHistoryAgreementById'
import { useApplyEditAgreement } from '../services/useApplyEditAgreement'
import { ROUTES } from '@/config/routes'
import { cleanHtmlContent } from '../utils/cleanHtmlContent'
import { getCleanDomain } from '@/utils/getCleanDomain'
import { getDomainIcon } from '../utils/domainIcon'

export default function useMinutaUser() {
  const navigate = useNavigate()
  const { id: paramId } = useParams<{ id: string }>()
  const user = useCurrentUser()
  const userId = paramId || user?.id || ''

  const [isSpeaking, setIsSpeaking] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  const [isDialogOpen, setIsDialogOpen] = useState(false)

  const { mutate: updateAgreement } = useUpdateAgreementUser()
  const {
    data: agreementData,
    error: agreementError,
    isLoading: isLoadingAgreement
  } = useFetchAgreementByIdUser(userId)
  const {
    data: signatureStatus = { data: { isModifica: false, check_testemunha_1: false, check_testemunha_2: false } },
    error: signatureError,
    isLoading: isLoadingSignature
  } = useFetchSignatureByIdUser(userId)
  const { data: historySuggestions = [], isLoading: isLoadingHistory } = useFetchHistoryAgreementById(agreementData?.id)
  const { mutate: applyEdit } = useApplyEditAgreement()

  useEffect(() => {
    window.speechSynthesis.cancel()
    setIsSpeaking(false)
  }, [userId])

  useEffect(() => {
    if (
      agreementData?.id &&
      agreementData?.isAssinado === false &&
      signatureStatus?.data?.isModifica === false &&
      user?.id &&
      userId === user.id
    ) {
      handleUpdateAgreement()
    }
  }, [agreementData?.id, agreementData?.isAssinado, signatureStatus?.data?.isModifica, user?.id, userId])

  const htmlContent = typeof agreementData?.html === 'string' ? agreementData.html : ''
  const fullStyledHtml = cleanHtmlContent(htmlContent)
  const editableHtml = htmlContent

  const shouldShowLoading = useMemo(() => {
    return isLoadingAgreement || isLoadingSignature || isLoadingHistory
  }, [isLoadingAgreement, isLoadingSignature, isLoadingHistory])

  const selectedIcon = getDomainIcon(getCleanDomain())

  const handleSpeak = () => {
    if (isSpeaking) return

    const utterance = new SpeechSynthesisUtterance()
    utterance.text = fullStyledHtml.replace(/<[^>]+>/g, '')
    utterance.lang = 'pt-BR'
    utterance.rate = 1
    utterance.onend = () => setIsSpeaking(false)

    setIsSpeaking(true)
    window.speechSynthesis.speak(utterance)
  }

  const handleStop = () => {
    window.speechSynthesis.cancel()
    setIsSpeaking(false)
  }

  const handleUpdateAgreement = async () => {
    try {
      await new Promise<void>((resolve, reject) => {
        updateAgreement(undefined, {
          onSuccess: () => {
            setMessage({ type: 'success', text: 'Atualização do contrato realizada com sucesso!' })
            setIsDialogOpen(true)
            resolve()
          },
          onError: error => {
            setMessage({ type: 'error', text: `Erro ao atualizar contrato: ${error}` })
            setIsDialogOpen(true)
            reject(error)
          }
        })
      })
    } catch (error) {
      console.error('Erro ao atualizar o contrato:', error)
    }
  }

  const handleSave = async (cleanHtml: string, idSuggestion: string) => {
    applyEdit(
      { id_edit: idSuggestion, html: cleanHtml },
      {
        onSuccess: () => {
          setMessage({ type: 'success', text: 'Edição do contrato salva com sucesso!' })
          setIsDialogOpen(true)
        },
        onError: error => {
          setMessage({ type: 'error', text: `Erro ao salvar edição do contrato: ${error}` })
          setIsDialogOpen(true)
        }
      }
    )
  }

  const handleBack = () => {
    if (user?.typeUser === 'ADMIN') {
      navigate(ROUTES.ADMIN_HOME.path)
      return
    }

    navigate(ROUTES.DASHBOARD_HOME.path)
  }

  return {
    agreementData,
    agreementError,
    editableHtml,
    fullStyledHtml,
    handleBack,
    handleSave,
    handleSpeak,
    handleStop,
    handleUpdateAgreement,
    historySuggestions,
    isDialogOpen,
    isSpeaking,
    message,
    selectedIcon,
    setIsDialogOpen,
    shouldShowLoading,
    signatureError,
    signatureStatus,
    user,
    userId
  }
}
